<script setup>
import { computed } from 'vue';
import GameImage from '@/components/live_casino_v2/GameImage.vue';

/**
 * Renders the live casino tables configured on a widget.
 *
 * GameImage owns the image fallback, the access gating and the navigation, so this
 * component only lays the cards out.
 */
const props = defineProps({
	widget: { type: Object, required: true },
});

const tables = computed(() => props.widget.items || []);

const tableFor = (item) => (item.media ? { ...item, image_url: item.media.path } : item);
</script>

<template>
	<div class="marketing-table-grid">
		<div v-for="item in tables" :key="item.id" class="marketing-table-card">
			<div class="marketing-table-thumb">
				<GameImage :game="tableFor(item)" size="extra-small" aspect_ratio="wide" />
			</div>
			<div class="marketing-table-name">{{ item.name }}</div>
			<div v-if="item.provider_name" class="marketing-table-provider">{{ item.provider_name }}</div>
		</div>
	</div>
</template>

<style scoped>
/*
 * The placement is a narrow column on some pages and the full width on others, so
 * cards per row follow the available width. The floor is the theme's own card width
 * in the narrowest of those columns.
 */
.marketing-table-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(130px, 100%), 1fr));
	gap: 10px;
	width: 100%;
}
.marketing-table-card {
	min-width: 0;
	cursor: pointer;
}
/*
 * GameImage sizes itself from an aspect ratio and, left in the flow, resolves its
 * height against the row and squeezes out the caption. A box owning the ratio keeps
 * it out of the flow.
 */
.marketing-table-thumb {
	position: relative;
	width: 100%;
	aspect-ratio: 4 / 3;
	overflow: hidden;
	border-radius: var(--rounded-md, 6px);
}
.marketing-table-thumb :deep(.image-container) {
	position: absolute;
	inset: 0;
	height: 100%;
	aspect-ratio: auto;
}
.marketing-table-name {
	margin-top: 6px;
	font-size: 12px;
	line-height: 1.3;
	font-weight: 500;
	color: var(--white, #fff);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.marketing-table-provider {
	font-size: 10px;
	line-height: 1.3;
	color: var(--text-secondary-color, #9a9a9a);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>
